import { jsx, jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import MDialog from "@mui/material/Dialog";
import MDialogActions from "@mui/material/DialogActions";
import MDialogContent from "@mui/material/DialogContent";
import MDialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";
import isFunction from "lodash/isFunction";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import React, { useEffect, useState } from "react";
import { isFirefox } from "react-device-detect";
import { useCurrentColor } from "../../shared/hooks/current-color";
import { getMultilineEllipsisSX } from "../../shared/utils/styles";
import THEME, { DION_SCROLLBAR } from "../../theme";
import Button from "../Button";
import { ArrowLeftIcon, CloseIcon } from "@dion-web/ui-icons";
var EDialogSize = /* @__PURE__ */ ((EDialogSize2) => {
  EDialogSize2["S"] = "s";
  EDialogSize2["M"] = "m";
  return EDialogSize2;
})(EDialogSize || {});
const SCROLL_PADDING = THEME.scroll.spacing.sidePadding;
const SCROLL_WIDTH = THEME.scroll.size.m;
const {
  minHeight: DIALOG_MIN_HEIGHT_S,
  width: DIALOG_WIDTH_S,
  maxHeight: DIALOG_MAX_HEIGHT_S
} = THEME.popup.size.s;
const {
  minHeight: DIALOG_MIN_HEIGHT_M,
  width: DIALOG_WIDTH_M,
  maxHeightVh: DIALOG_MAX_HEIGHT_VH_M
} = THEME.popup.size.m;
const HIDDEN_SCROLL_BAR = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  webkitScrollbar: {
    width: "0px",
    background: "transparent"
  }
};
const MAX_CONTROLS = 2;
const SPACING = THEME.popup.spacing;
const DIALOG_MARGIN = SPACING.margin;
const MAX_INNER_VOLUME_S = (DIALOG_MAX_HEIGHT_S - DIALOG_MARGIN * 2) * (DIALOG_WIDTH_S - DIALOG_MARGIN * 2);
const CLOSE_ICON_SX = {
  float: "right",
  marginRight: `-${SPACING.margin - SPACING.closeOffset}px`,
  marginTop: `-${SPACING.margin - SPACING.closeOffset}px`
};
const DIVIDER_SX = {
  minWidth: "100%",
  marginLeft: "-32px",
  marginRight: "-32px",
  height: "1px"
};
const DIVIDER_TOP_SX = {
  ...DIVIDER_SX,
  "&::after": {
    content: '""',
    position: "absolute",
    height: "4px",
    width: "100%",
    background: "linear-gradient(color-mix(in srgb, black 10%, transparent), transparent)",
    zIndex: 20,
    marginTop: "1px"
  }
};
const DIVIDER_BOTTOM_SX = {
  ...DIVIDER_SX,
  "&::before": {
    content: '""',
    position: "absolute",
    height: "2px",
    width: "100%",
    background: "linear-gradient(transparent, color-mix(in srgb, black 10%, transparent))",
    marginTop: "-2px",
    zIndex: 20
  }
};
const DEFAULT_COLOR = "elevationObject";
const TITLE_MAX_LINES = 3;
const SUBTITLE_MAX_LINES = 3;
const TITLE_SX = {
  wordWrap: "break-word",
  maxHeight: "100px",
  ...getMultilineEllipsisSX(TITLE_MAX_LINES)
};
const SUBTITLE_SX = {
  wordWrap: "break-word",
  marginTop: `${SPACING.subtitleTopMargin}px !important`,
  ...getMultilineEllipsisSX(SUBTITLE_MAX_LINES)
};
const StyledDialog = styled(MDialog)(
  ({ theme, size }) => {
    const isM = size === "m";
    return {
      "& .MuiDialog-paper": {
        backgroundColor: theme.palette[DEFAULT_COLOR].main,
        borderRadius: `${theme.dialog.borderRadius}px`,
        boxSizing: "border-box",
        minHeight: `${isM ? DIALOG_MIN_HEIGHT_M : DIALOG_MIN_HEIGHT_S}px`,
        maxHeight: `${DIALOG_MAX_HEIGHT_VH_M}vh`,
        width: `${isM ? DIALOG_WIDTH_M : DIALOG_WIDTH_S}px`,
        minWidth: `${DIALOG_WIDTH_S}px`,
        padding: `${DIALOG_MARGIN}px`,
        backgroundImage: "none",
        boxShadow: "none",
        ...HIDDEN_SCROLL_BAR,
        overflowX: "hidden"
      }
    };
  }
);
const StyledDialogTitle = styled(MDialogTitle)(
  ({ withShadow }) => ({
    padding: "0",
    "&.MuiDialogTitle-root": {
      paddingBottom: `${SPACING.titleBottomMargin}px`,
      boxSizing: "border-box",
      width: "100%",
      wordWrap: "break-word"
    }
  })
);
const StyledDialogContent = styled(MDialogContent)(
  () => ({
    overflowY: "scroll",
    overflowX: "hidden",
    ...DION_SCROLLBAR,
    padding: "0 0 1px 0",
    // этот 1px принципально важен для ФФ - иначе появляется паразитный скроллбар. :(
    paddingRight: `${SCROLL_PADDING}px`,
    marginRight: `-${SCROLL_PADDING + SCROLL_WIDTH + (isFirefox ? 6 : 0)}px`,
    wordWrap: "break-word"
  })
);
const StyledDialogActions = styled(MDialogActions)(
  ({ withShadow }) => {
    return {
      display: "flex",
      alignItems: "center",
      padding: "0",
      "&.MuiDialogActions-root": {
        paddingTop: `${SPACING.actionsTopMargin}px`,
        boxSize: "border-box",
        display: "flex",
        gap: "16px"
        // TODO
      },
      "&.MuiDialogActions-root>:not(style)~:not(style)": {
        marginLeft: 0,
        marginRight: 0,
        gap: "16px"
        // TODO
      }
    };
  }
);
const nonEmpty = (item) => !isUndefined(item) && !isNull(item);
const Dialog = (props) => {
  const {
    title,
    subTitle,
    controls,
    onOpen,
    onClose,
    onBackClick,
    open,
    shadeContent,
    maxControls,
    size
  } = props;
  const buttonSpacing = THEME.buttonGroup.spacing.m;
  const [dialogSize, setDialogSize] = useState(size || "s" /* S */);
  const [dialogRef, setDialogRef] = useState();
  const [contentRef, setContentRef] = useState();
  const [contentWrapperRef, setContentWrapperRef] = useState();
  const [titleRef, setTitleRef] = useState();
  const [controlsRef, setControlsRef] = useState();
  const [isScrollbarVisible, setScrollVisible] = useState(false);
  const allControls = React.useMemo(
    () => (controls || []).filter(nonEmpty).slice(0, maxControls ?? MAX_CONTROLS),
    [controls, dialogSize, maxControls]
  );
  const [prevOpen, setPrevOpen] = React.useState(open);
  const hasBackButton = isFunction(onBackClick);
  const { ctx, Provider } = useCurrentColor("elevationObject", "contrast", true);
  React.useEffect(() => {
    if (!prevOpen && open && isFunction(onOpen))
      onOpen();
    setPrevOpen(open);
  }, [open]);
  useEffect(() => {
    const dialogBoxRef = dialogRef?.querySelector(".MuiDialog-paper");
    const dialogHeight = dialogBoxRef?.clientHeight;
    const titleRealHeight = titleRef?.clientHeight || 0;
    const contentRealHeight = contentWrapperRef?.clientHeight || 0;
    const controlsRealHeight = controlsRef?.clientHeight || 0;
    const contentWidth = contentWrapperRef?.clientWidth || 0;
    const realHeight = contentRealHeight + titleRealHeight + controlsRealHeight;
    const scrollbarVisible = contentRef && contentRef?.scrollHeight > contentRef?.clientHeight || false;
    const contentVolume = realHeight * contentWidth;
    if (isScrollbarVisible !== scrollbarVisible)
      setScrollVisible(scrollbarVisible);
    if (!dialogRef || !dialogHeight || size)
      return;
    if (dialogSize === "s" /* S */ && dialogHeight >= DIALOG_MIN_HEIGHT_M && contentVolume > MAX_INNER_VOLUME_S)
      setDialogSize("m" /* M */);
    if (dialogSize === "m" /* M */ && contentVolume <= MAX_INNER_VOLUME_S)
      setDialogSize("s" /* S */);
  });
  useEffect(() => {
    if (size)
      setDialogSize(size);
  }, [size]);
  useEffect(() => {
    if (dialogRef)
      dialogRef.title = "";
  }, [dialogRef]);
  return /* @__PURE__ */ jsx(Provider, { value: ctx, children: /* @__PURE__ */ jsxs(
    StyledDialog,
    {
      ...props,
      ref: setDialogRef,
      size: dialogSize,
      open: open || false,
      children: [
        /* @__PURE__ */ jsxs(StyledDialogTitle, { withShadow: isScrollbarVisible, ref: setTitleRef, children: [
          /* @__PURE__ */ jsx(Box, { sx: CLOSE_ICON_SX, children: /* @__PURE__ */ jsx(Button, { variant: "ghost", startIcon: /* @__PURE__ */ jsx(CloseIcon, {}), size: "m", onClick: onClose }) }),
          (title || subTitle) && /* @__PURE__ */ jsxs(Stack, { spacing: `${SPACING.titleBottomMargin}px`, children: [
            title && /* @__PURE__ */ jsx(Typography, { variant: "h4", sx: TITLE_SX, children: title }),
            subTitle && /* @__PURE__ */ jsx(Typography, { variant: "body1", color: "lowContrast", sx: SUBTITLE_SX, children: subTitle })
          ] })
        ] }),
        isScrollbarVisible && /* @__PURE__ */ jsx(Divider, { sx: shadeContent ? DIVIDER_TOP_SX : DIVIDER_SX }),
        /* @__PURE__ */ jsx(StyledDialogContent, { ref: setContentRef, children: /* @__PURE__ */ jsx("div", { ref: setContentWrapperRef, children: props.children }) }),
        isScrollbarVisible && /* @__PURE__ */ jsx(Divider, { sx: shadeContent ? DIVIDER_BOTTOM_SX : DIVIDER_SX }),
        (!!controls || hasBackButton) && /* @__PURE__ */ jsxs(StyledDialogActions, { withShadow: isScrollbarVisible, ref: setControlsRef, gap: buttonSpacing, children: [
          hasBackButton && /* @__PURE__ */ jsx(Stack, { direction: "row", flexShrink: 1, children: /* @__PURE__ */ jsx(
            Button,
            {
              onClick: onBackClick,
              variant: "secondary",
              startIcon: /* @__PURE__ */ jsx(ArrowLeftIcon, {}),
              size: "m",
              alignSelf: "flex-end",
              children: dialogSize === "s" /* S */ ? void 0 : "Назад"
            }
          ) }),
          /* @__PURE__ */ jsx(Stack, { direction: "row-reverse", gap: `${buttonSpacing}px`, flexGrow: 1, overflow: "hidden", ml: 0, children: allControls })
        ] })
      ]
    }
  ) });
};
var Dialog_default = Dialog;
export {
  EDialogSize,
  Dialog_default as default
};
